<template>
  <div>
    <data-table
      ref="datatableRef"
      :config="datatableConfig"
      :endpoint="endpoint"
      :filterConfig="filterConfig"
      :refreshButton="refreshButtonConfig"
      :adjustColumnsWidth="adjustColumnsWidthConfig"
      :pdfButton="pdfButtonConfig"
      :csvButton="csvButtonConfig"
      :clipboardButton="clipboardButtonConfig"
      :printButton="printButtonConfig"
      @data-loaded="handleDataLoaded"
      @data-error="handleDataError"
      @row-click="handleRowClick"
      @edit-click="handleEditClick"
      @button-click="handleButtonClick">
    </data-table>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { eventBus } from '../../eventBus';
import { useDevicesFranquias } from '../../composables/useDevicesFranquias';
import { useConfirmAction } from '../../composables/useConfirmAction';
import { log } from '../../utils/DebugLogger';
import DataTable from '../../grid/DataTable.vue';

type DeviceId = number;

interface DataTableInstance {
  loadData: () => Promise<void> | void;
}

interface DeviceRowData {
  device_id: DeviceId;
  device_name?: string;
  device_token?: string;
  provider_type?: string;
  connection_status?: string;
  [key: string]: unknown;
}

interface CellNode {
  rowPinned?: boolean;
}

interface CellParams {
  node?: CellNode;
  value?: unknown;
}

interface ButtonClickPayload {
  value?: unknown;
  data?: DeviceRowData;
  action?: string;
}

withDefaults(defineProps<{
  usuarioVersao?: number;
}>(), {
  usuarioVersao: 0
});

const emit = defineEmits<{
  'edit-device': [deviceId: DeviceId];
  'register-device': [rowData: DeviceRowData];
}>();

// Get i18n composable
const { t } = useI18n();
const { confirmAction } = useConfirmAction();

// Get whatsapp franquias composable
const { removerDevice, fetchDeviceToken, disconnectDevice, toggleActive, toggleWebsocket, reconnectDevice } = useDevicesFranquias();

// Reactive data
const datatableRef = ref<DataTableInstance | null>(null);

// Computed properties
const endpoint = computed(() => '/index.php/config/devices_franquias/carrega_devices_franquias');

const tableColumns = computed(() => {
  return [
    {
      data: 'device_id',
      title: t('ID'),
      width: 100,
      resizable: true,
      sortable: true,
      renderType: 'clickable_id'
    },
    {
      data: 'provider_type',
      title: t('Tipo'),
      width: 120,
      resizable: true,
      sortable: true,
      cellRenderer: (params: CellParams) => {
        const type = (params.value as string) || 'whatsapp';
        const typeMap: Record<string, { label: string; color: string; bg: string; icon: string }> = {
          'whatsapp': { label: 'WhatsApp', color: '#22c55e', bg: '#f0fdf4', icon: 'pi-whatsapp' },
          'instagram': { label: 'Instagram', color: '#a855f7', bg: '#faf5ff', icon: 'pi-instagram' }
        };
        const info = typeMap[type] || typeMap['whatsapp'];
        return `<span style="display:inline-flex;align-items:center;gap:6px;padding:4px 10px;border-radius:12px;font-size:12px;font-weight:600;color:${info.color};background:${info.bg};"><i class="pi ${info.icon}" style="font-size:12px;"></i>${info.label}</span>`;
      }
    },
    {
      data: 'franquias_nomes',
      title: t('Franquias'),
      width: 200,
      resizable: true,
      sortable: true,
      cellRenderer: (params: CellParams) => {
        const value = params.value || t('Nenhuma franquia associada');
        return value;
      }
    },
    {
      data: 'numero_whatsapp',
      title: t('Número / Conta'),
      width: 180,
      resizable: true,
      sortable: true
    },
    {
      data: 'device_name',
      title: t('Nome do Dispositivo'),
      width: 180,
      resizable: true,
      sortable: true
    },
    {
      data: 'device_token',
      title: t('Device Token'),
      width: 180,
      resizable: true,
      sortable: true
    },
    {
      data: 'active',
      title: t('Ativo'),
      width: 80,
      resizable: true,
      sortable: true,
      renderType: 'toggle',
      toggleCallback: async (rowData: DeviceRowData, newValue: boolean) => {
        try {
          await toggleActive(rowData.device_id, newValue);
          eventBus.emit('globalMessage.success', newValue ? t('Dispositivo ativado!') : t('Dispositivo desativado!'));
        } catch (error) {
          log('DevicesFranquiasTable', 'toggleActive', 'Error', error);
          eventBus.emit('globalMessage.error', t('Erro ao atualizar status'));
          eventBus.emit('devices-franquias.refresh');
        }
      }
    },
    {
      data: 'websocket_enabled',
      title: t('WebSocket'),
      width: 100,
      resizable: true,
      sortable: true,
      renderType: 'toggle',
      toggleCallback: async (rowData: DeviceRowData, newValue: boolean) => {
        if ((rowData.provider_type || 'whatsapp') !== 'whatsapp') return;
        if (!rowData.device_name || rowData.connection_status === 'not_found') {
          eventBus.emit('globalMessage.error', t('Dispositivo precisa estar registrado na Evolution API'));
          eventBus.emit('devices-franquias.refresh');
          return;
        }
        try {
          await toggleWebsocket(rowData.device_name, newValue);
          eventBus.emit('globalMessage.success', newValue ? t('WebSocket ativado!') : t('WebSocket desativado!'));
        } catch (error) {
          log('DevicesFranquiasTable', 'toggleWebsocket', 'Error', error);
          eventBus.emit('globalMessage.error', (error as Error).message || t('Erro ao configurar WebSocket'));
          eventBus.emit('devices-franquias.refresh');
        }
      }
    },
    {
      data: 'connection_status',
      title: t('Status Conexão'),
      width: 160,
      resizable: true,
      sortable: true,
      cellRenderer: (params: CellParams) => {
        const status = (params.value as string) || '';
        const statusMap: Record<string, { label: string; color: string; bg: string; icon: string }> = {
          'open': { label: t('Conectado'), color: '#22c55e', bg: '#f0fdf4', icon: 'pi-check-circle' },
          'close': { label: t('Desconectado'), color: '#ef4444', bg: '#fef2f2', icon: 'pi-times-circle' },
          'connecting': { label: t('Conectando'), color: '#f59e0b', bg: '#fffbeb', icon: 'pi-spin pi-spinner' },
          'not_found': { label: t('Não encontrado'), color: '#6b7280', bg: '#f9fafb', icon: 'pi-question-circle' }
        };
        const info = statusMap[status];
        if (!info) return '<span style="color: #9ca3af;">—</span>';
        return `<span style="display:inline-flex;align-items:center;gap:6px;padding:4px 10px;border-radius:12px;font-size:12px;font-weight:600;color:${info.color};background:${info.bg};"><i class="pi ${info.icon}" style="font-size:12px;"></i>${info.label}</span>`;
      }
    },
    {
      data: null,
      title: t('Ações'),
      width: 300,
      resizable: true,
      sortable: false,
      renderType: 'action_buttons',
      buttons: [
        {
          text: t('Remover'),
          icon: 'pi pi-trash',
          class: 'p-button-danger p-button-sm',
          action: 'delete',
          condition: (row: Record<string, unknown>) => row['connection_status'] !== 'open' && row['connection_status'] !== 'connecting'
        },
        {
          text: t('Registrar Device'),
          icon: 'pi pi-mobile',
          class: 'p-button-success p-button-sm',
          action: 'register-device',
          condition: (row: Record<string, unknown>) => (row['provider_type'] || 'whatsapp') === 'whatsapp' && (!row['device_token'] || String(row['device_token']).trim() === '') && row['connection_status'] !== 'open'
        },
        {
          text: t('Buscar Token'),
          icon: 'pi pi-download',
          class: 'p-button-info p-button-sm',
          action: 'fetch-token',
          condition: (row: Record<string, unknown>) => (row['provider_type'] || 'whatsapp') === 'whatsapp' && row['connection_status'] === 'open' && (!row['device_token'] || String(row['device_token']).trim() === '')
        },
        {
          text: t('Reconectar'),
          icon: 'pi pi-refresh',
          class: 'p-button-info p-button-sm',
          action: 'reconnect',
          condition: (row: Record<string, unknown>) => (row['provider_type'] || 'whatsapp') === 'whatsapp' && row['connection_status'] === 'open' && !!row['device_name']
        },
        {
          text: t('Desconectar'),
          icon: 'pi pi-power-off',
          class: 'p-button-warning p-button-sm',
          action: 'disconnect',
          condition: (row: Record<string, unknown>) => (row['provider_type'] || 'whatsapp') === 'whatsapp' && row['connection_status'] === 'open'
        }
      ]
    }
  ];
});

const datatableConfig = computed(() => {
  return {
    tableId: 'tabelaDevicesFranquias',
    title: t('Dispositivos'),
    filename: `devices_franquias_${new Date().toISOString().split('T')[0]}`,
    columns: tableColumns.value,
    pageLength: 100,
    height: '500px',
    footer: false,
    rowSelection: 'single',
    rowTestId: (data: Record<string, unknown>) => {
      const id = data?.['device_id'];
      return id == null ? '' : `devices-franquias-row-${id}`;
    }
  };
});

const filterConfig = computed(() => {
  return {
    searchName: t('Procurar'),
    choosePlaceholder: t('Escolha'),
    refreshName: t('Atualizar'),
    printName: t('Imprimir'),
    debug: false,
    columns: {
      'device_id': 'text',
      'provider_type': 'text',
      'franquias_nomes': 'text',
      'numero_whatsapp': 'text',
      'device_name': 'text',
      'device_token': 'text',
      'active': 'text',
      'websocket_enabled': 'text',
      'connection_status': 'text'
    }
  };
});

const refreshButtonConfig = computed(() => {
  return {
    show: true,
    text: t('Atualizar'),
    loading: false
  };
});

const adjustColumnsWidthConfig = computed(() => {
  return {
    show: true
  };
});

const pdfButtonConfig = computed(() => {
  return {
    show: true,
    text: t('PDF'),
    loading: false
  };
});

const csvButtonConfig = computed(() => {
  return {
    show: true,
    text: t('CSV'),
    loading: false
  };
});

const clipboardButtonConfig = computed(() => {
  return {
    show: true,
    text: t('Copiar'),
    loading: false
  };
});

const printButtonConfig = computed(() => {
  return {
    show: true,
    text: t('Imprimir'),
    loading: false
  };
});

// Methods
const loadData = async (): Promise<void> => {
  log('DevicesFranquiasTable', 'loadData', 'Refreshing table data');
  if (datatableRef.value) {
    await datatableRef.value.loadData();
  }
};

const handleDataLoaded = (data: unknown): void => {
  log('DevicesFranquiasTable', 'handleDataLoaded', 'Data loaded successfully', data);
};

const handleDataError = (error: Error): void => {
  log('DevicesFranquiasTable', 'handleDataError', 'Error loading data', error);
  eventBus.emit('globalMessage.error', t('Erro ao carregar dados'));
};

const handleRowClick = (rowData: DeviceRowData): void => {
  log('DevicesFranquiasTable', 'handleRowClick', 'Row clicked', rowData);
};

const handleEditClick = (deviceId: DeviceId): void => {
  log('DevicesFranquiasTable', 'handleEditClick', 'Edit clicked for ID', deviceId);
  emit('edit-device', deviceId);
};

const handleDelete = async (deviceId: DeviceId): Promise<void> => {
  log('DevicesFranquiasTable', 'handleDelete', 'Delete clicked for ID', deviceId);

  confirmAction({
    message: t('Tem certeza que deseja remover este registro?'),
    header: t('Confirmar Exclusão'),
    acceptLabel: t('Sim'),
    rejectLabel: t('Não'),
    onAccept: async () => {
      try {
        await removerDevice(deviceId);

        eventBus.emit('globalMessage.success', t('Registro removido com sucesso!'));
        eventBus.emit('devices-franquias.refresh');
      } catch (error) {
        log('DevicesFranquiasTable', 'handleDelete', 'Error deleting record', error);
        eventBus.emit('globalMessage.error', t('Erro ao remover registro'));
      }
    }
  });
};

const handleRegisterDevice = (rowData: DeviceRowData): void => {
  log('DevicesFranquiasTable', 'handleRegisterDevice', 'Register device clicked', rowData);
  emit('register-device', rowData);
};

const handleFetchToken = async (rowData: DeviceRowData): Promise<void> => {
  log('DevicesFranquiasTable', 'handleFetchToken', 'Fetch token clicked', rowData);
  if (!rowData.device_name) return;

  try {
    await fetchDeviceToken(rowData.device_id, rowData.device_name);
    eventBus.emit('globalMessage.success', t('Device token atualizado com sucesso!'));
    eventBus.emit('devices-franquias.refresh');
  } catch (error) {
    log('DevicesFranquiasTable', 'handleFetchToken', 'Error fetching token', error);
    eventBus.emit('globalMessage.error', (error as Error).message || t('Erro ao buscar device token'));
  }
};

const handleDisconnect = async (rowData: DeviceRowData): Promise<void> => {
  log('DevicesFranquiasTable', 'handleDisconnect', 'Disconnect clicked', rowData);

  confirmAction({
    message: t('Tem certeza que deseja desconectar este dispositivo?'),
    header: t('Confirmar Desconexão'),
    acceptLabel: t('Sim'),
    rejectLabel: t('Não'),
    onAccept: async () => {
      if (!rowData.device_name) return;
      try {
        await disconnectDevice(rowData.device_name);
        eventBus.emit('globalMessage.success', t('Dispositivo desconectado com sucesso!'));
        eventBus.emit('devices-franquias.refresh');
      } catch (error) {
        log('DevicesFranquiasTable', 'handleDisconnect', 'Error disconnecting', error);
        eventBus.emit('globalMessage.error', t('Erro ao desconectar dispositivo'));
      }
    }
  });
};

const handleReconnect = async (rowData: DeviceRowData): Promise<void> => {
  log('DevicesFranquiasTable', 'handleReconnect', 'Reconnect clicked', rowData);
  if (!rowData.device_name) return;

  try {
    await reconnectDevice(rowData.device_name);
    eventBus.emit('globalMessage.success', t('Reconexão iniciada! Aguarde alguns segundos.'));
    setTimeout(() => eventBus.emit('devices-franquias.refresh'), 5000);
  } catch (error) {
    log('DevicesFranquiasTable', 'handleReconnect', 'Error reconnecting', error);
    eventBus.emit('globalMessage.error', (error as Error).message || t('Erro ao reconectar dispositivo'));
  }
};

const handleButtonClick = ({ data, action }: ButtonClickPayload): void => {
  log('DevicesFranquiasTable', 'handleButtonClick', 'Button clicked', { action, data });
  if (!data) return;
  if (action === 'delete') {
    handleDelete(data.device_id);
  } else if (action === 'register-device') {
    handleRegisterDevice(data);
  } else if (action === 'fetch-token') {
    handleFetchToken(data);
  } else if (action === 'disconnect') {
    handleDisconnect(data);
  } else if (action === 'reconnect') {
    handleReconnect(data);
  }
};

// Lifecycle
onMounted(() => {
  log('DevicesFranquiasTable', 'mounted', 'Component mounted');

  // Listen to refresh events
  eventBus.on('devices-franquias.refresh', loadData);
  eventBus.on('filter.updated', loadData);
});

onBeforeUnmount(() => {
  log('DevicesFranquiasTable', 'beforeUnmount', 'Cleaning up event listeners');

  // Clean up event listeners
  eventBus.off('devices-franquias.refresh', loadData);
  eventBus.off('filter.updated', loadData);
});

// Expose methods for parent components
defineExpose({
  loadData
});
</script>

<style scoped>
/* Add any table-specific styles here */
</style>
